import { createContext, useContext, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { t, ta } from "../lib/sprache";

/**
 * Nur-Lesen-Phase einer Agentur (gesperrt oder gekündigt, 0042) in der Oberfläche (RECHTE-8): Wege zum Anlegen, Importieren,
 * Einladen, Bearbeiten und Einreichen zeigen statt eines Formulars, das sicher scheitert, einen Hinweis; Knöpfe dorthin
 * blendet portal.css unter `.tk-nur-lesen` aus (Links nach Ziel, sonstige Schreibknöpfe über `data-schreiben`). Lesen,
 * Export und Belege bleiben. Die Datenbank sperrt Änderungen ohnehin (app.guard_org_readonly).
 */
export const NurLesenContext = createContext(false);
export function useNurLesen(): boolean {
  return useContext(NurLesenContext);
}
/** 0414 (P-4): Ende einer laufenden Abo-Pause (formatiertes Datum), sonst null; gesperrte Bereiche nennen dann die Pause */
export const NurLesenPauseContext = createContext<string | null>(null);
export function useNurLesenPause(): string | null {
  return useContext(NurLesenPauseContext);
}

const SCHREIBWEGE = /\/(neu|import|einladen|bearbeiten|einreichen|erinnern)(\/|$)/;
export function istSchreibweg(path: string): boolean {
  return SCHREIBWEGE.test(path);
}

/**
 * Schreibbereich einer Seite (RECHTE-R3-3): in der Nur-Lesen-Phase ein Hinweis und alle Felder und Knöpfe darin gesperrt
 * (fieldset disabled, ohne eigenes Layout). Außerhalb der Nur-Lesen-Phase unverändert. Links (Lesen, Export) bleiben nutzbar.
 */
export function NurLesenFeld({ children, hinweis = true }: { children: ReactNode; hinweis?: boolean }) {
  const aktiv = useNurLesen();
  const pauseBis = useContext(NurLesenPauseContext);
  if (!aktiv) return <>{children}</>;
  return (
    <>
      {hinweis && <p className="notice" role="status">{nurLesenSatz(pauseBis)}</p>}
      <fieldset className="tk-lesefeld" disabled>{children}</fieldset>
    </>
  );
}
/** Ein Satz für gesperrte Bereiche des Agentur-Portals in der Nur-Lesen-Phase (das Creator-Portal hat eigene Sätze). */
export function nurLesenSatz(pauseBis: string | null = null): string {
  // 0414 (P-4): während einer Abo-Pause nicht „mit einem Abo“, sondern das Ende der Pause
  if (pauseBis) {
    return ta(`Pausiert bis ${pauseBis}: Ändern, Einladen, Unterschreiben und Übergeben gehen wieder nach der Pause oder sobald du sie unter Einstellungen · Abo & Tarif vorzeitig beendest. Lesen, Herunterladen und Export gehen weiter.`,
      `Paused until ${pauseBis}: changing, inviting, signing and handing over work again after the pause or as soon as you end it early under Settings · Subscription & plan. Reading, downloading and export still work.`, `Pausiert bis ${pauseBis}: Ändern und Unterschreiben gehen wieder nach der Pause oder sobald du sie unter Einstellungen · Abo & Tarif vorzeitig beendest. Lesen, Herunterladen und Export gehen weiter.`, `Paused until ${pauseBis}: changing and signing work again after the pause or as soon as you end it early under Settings · Subscription & plan. Reading, downloading and export still work.`);
  }
  return ta("Nur lesen: Diese Agentur nutzt CreatorOffice derzeit nur lesend. Ändern, Einladen, Unterschreiben und Übergeben gehen erst wieder mit einem Abo (Einstellungen · Abo & Tarif); Lesen, Herunterladen und Export gehen weiter.",
    "Read-only: this agency currently uses CreatorOffice read-only. Changing, inviting, signing and handing over work again with a subscription (Settings · Subscription & plan); reading, downloading and export still work.", "Nur lesen: Dein Konto ist derzeit nur lesbar. Ändern und Unterschreiben gehen erst wieder mit einem Abo (Einstellungen · Abo & Tarif); Lesen, Herunterladen und Export gehen weiter.", "Read-only: your account is currently read-only. Changing and signing work again with a subscription (Settings · Subscription & plan); reading, downloading and export still work.");
}

/**
 * K-2 (Abnahme 22.10.2026): gemeinsamer Hinweis für leere Listen und gesperrte Einladungen in Pause und Nur-Lesen:
 * „Nur lesen.“ plus Satz (ohne die doppelte Vorsilbe „Nur lesen:“ des Satzes).
 */
export function NurLesenHinweis({ className = "muted" }: { className?: string }) {
  const pauseBis = useContext(NurLesenPauseContext);
  const satz = nurLesenSatz(pauseBis).replace(/^(Nur lesen|Read-only): (.)/, (_m, _v: string, c: string) => c.toUpperCase());
  return <p className={className} role="note"><strong>{t("Nur lesen.", "Read-only.")}</strong> {satz}</p>;
}

/**
 * K-1 (Abnahme 22.10.2026): Schreibaktion (Eintrag im „Mehr“-Menü, Nebenknopf), die in Pause und Nur-Lesen ganz entfällt,
 * wie „Neu“. Für Links ohne Knopf-Klasse greift die Regel in portal.css nicht, deshalb hier in React.
 */
export function NurSchreiben({ children }: { children: ReactNode }) {
  return useNurLesen() ? null : <>{children}</>;
}

/**
 * `testende` (0323): Testende ohne Zahlungsart, für den Admin: statt „Nur lesen“ der Satz, dass Neuanlegen mit dem Tarif wieder
 * geht, „Mit <empfohlener Tarif> weitermachen“ und „Schließen“. Löschen steht hier nie (Weitermachen und Löschen nie im selben
 * Knopf); Kündigen und Löschen bleiben unverändert in den Einstellungen unter Abo & Tarif.
 */
export function NurLesenRahmen({ aktiv, creator = false, en = false, testende = null, weiter = null, pause = null, children }: {
  aktiv: boolean; creator?: boolean; en?: boolean; testende?: { label: string; to: string; text: string } | null;
  /** K-486: nur für den Admin: Weitermachen im bisherigen Tarif („Weiter mit Team für 129,00 € netto im Monat“, Wechseldialog) */
  weiter?: { label: string; to: string } | null;
  /** 0414 (P-4): laufende Abo-Pause: Ende (formatiert) und für den Admin „Pause vorzeitig beenden“ */
  pause?: { bis: string; beenden?: (() => Promise<void>) | null } | null;
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [beendet, setBeendet] = useState<"" | "laeuft" | "fehler">("");
  if (aktiv && pause && istSchreibweg(pathname)) {
    const tc = (de: string, e: string) => (en ? e : t(de, e));
    const schliessen = () => { if (window.history.length > 1) void navigate(-1); else void navigate(creator ? "/creator/rechnungen" : "/agentur"); };
    return (
      <NurLesenContext.Provider value>
        <main id="main" className="content">
          <div className="panel stack tk-empty" role="status">
            <h1 className="tk-state-title">{tc(`Pausiert bis ${pause.bis}`, `Paused until ${pause.bis}`)}</h1>
            <p>{creator
              ? tc("Deine Agentur hat CreatorOffice pausiert. Bis dahin sind neue Rechnungen, Nachrichten und Unterschriften nicht möglich; deine Belege kannst du weiter ansehen und herunterladen.",
                "Your agency has paused CreatorOffice. Until then new invoices, messages and signatures are not possible; you can still view and download your documents.")
              : t("Während der Pause ist das Konto nur lesbar. Lesen und Export gehen weiter; deine Daten sind vollständig da.", "During the pause the account is read-only. Reading and export still work; your data is all still there.")}</p>
            {beendet === "fehler" && <p className="tk-error" role="alert">{t("Das hat nicht geklappt. Bitte unter Einstellungen · Abo & Tarif erneut versuchen.", "That did not work. Please try again under Settings · Subscription & plan.")}</p>}
            <div className="row">
              {!creator && pause.beenden && (
                <button type="button" className="primary" disabled={beendet === "laeuft"} aria-busy={beendet === "laeuft"}
                  onClick={() => { setBeendet("laeuft"); void pause.beenden!().then(() => setBeendet(""), () => setBeendet("fehler")); }}>
                  {beendet === "laeuft" ? t("Wird beendet …", "Ending …") : t("Pause vorzeitig beenden", "End pause early")}
                </button>
              )}
              <button type="button" onClick={schliessen}>{tc("Schließen", "Close")}</button>
            </div>
          </div>
        </main>
      </NurLesenContext.Provider>
    );
  }
  if (aktiv && istSchreibweg(pathname)) {
    const tc = (de: string, e: string) => (en ? e : t(de, e));
    const schliessen = () => { if (window.history.length > 1) void navigate(-1); else void navigate("/agentur"); };
    return (
      <NurLesenContext.Provider value>
        <main id="main" className="content">
          <div className="panel stack tk-empty" role="status">
            <h1 className="tk-state-title">{tc("Nur lesen", "Read-only")}</h1>
            {!creator && testende ? (
              <>
                <p>{testende.text}</p>
                <div className="row"><Link className="btn primary" to={testende.to}>{testende.label}</Link><button type="button" onClick={schliessen}>{t("Schließen", "Close")}</button></div>
              </>
            ) : (
              <>
                <p>{creator
                  ? tc("Deine Agentur nutzt CreatorOffice derzeit nur lesend. Neue Rechnungen, Nachrichten und Unterschriften sind nicht möglich; deine Belege kannst du weiter ansehen und herunterladen.",
                    "Your agency currently uses CreatorOffice read-only. New invoices, messages and signatures are not possible; you can still view and download your documents.")
                  : ta("Diese Agentur nutzt CreatorOffice derzeit nur lesend. Anlegen, Importieren und Ändern sind nicht möglich; Lesen und Export gehen weiter.", "This agency currently uses CreatorOffice read-only. Creating, importing and editing are not possible; reading and export still work.", "Dein Konto ist derzeit nur lesbar. Anlegen, Importieren und Ändern sind nicht möglich; Lesen und Export gehen weiter.", "Your account is currently read-only. Creating, importing and editing are not possible; reading and export still work.")}</p>
                {/* K-486: Weitermachen statt „Abo wählen oder Konto löschen“; Löschen steht nur in den Einstellungen */}
                {!creator && weiter && <p>{t("Deine Daten sind vollständig da. Mit einem Abo geht es sofort weiter, genau da, wo du aufgehört hast.", "Your data is all still there. With a subscription you carry on right away, exactly where you left off.")}</p>}
                {!creator && weiter ? (
                  <div className="row">
                    <Link className="btn primary" to={weiter.to}>{weiter.label}</Link>
                    <Link className="btn" to="/agentur/einstellungen/abo?wechsel=wahl">{t("Anderen Tarif wählen", "Choose another plan")}</Link>
                    <small><Link to="/agentur/einstellungen/export">{t("Daten exportieren", "Export data")}</Link></small>
                  </div>
                ) : (
                  <div className="row"><Link className="btn" to={creator ? "/creator/rechnungen" : "/agentur"}>{creator ? tc("Zu den Rechnungen", "Go to invoices") : t("Zur Übersicht", "Go to overview")}</Link></div>
                )}
              </>
            )}
          </div>
        </main>
      </NurLesenContext.Provider>
    );
  }
  return (
    <NurLesenContext.Provider value={aktiv}>
      <NurLesenPauseContext.Provider value={aktiv && pause ? pause.bis : null}>
        <div className={aktiv ? "tk-nur-lesen" : "tk-nur-lesen-aus"}>{children}</div>
      </NurLesenPauseContext.Provider>
    </NurLesenContext.Provider>
  );
}
